import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import {
  cuotaMeta,
  descripcionMeta,
  IMPORTES_META_MES,
  IMPORTES_META_UNA,
  metaInicial,
  METAS,
  nombreCofre,
  PLAZOS_META,
} from '#components/mobile/travesia/catalogo';
import type { MetaElegida } from '#components/mobile/travesia/catalogo';
import { DibujoMeta } from '#components/mobile/travesia/DibujosMetas';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, movimiento } from '#components/mobile/ui/tokens';
import { useLocale } from '#hooks/useLocale';

import {
  Chip,
  estiloCampo,
  Etiqueta,
  HojaTravesia,
  TituloHoja,
  useEuros,
} from './comun';

type HojaObjetivoProps = {
  /** Mes en curso `YYYY-MM` (para la fecha de «Una vez»). */
  mes: string;
  ocupado?: boolean;
  onGuardar: (meta: MetaElegida) => void;
};

/**
 * Paso 6 · «Tu primer objetivo»: cinco metas con dibujo, «Cada mes» o «Una
 * vez», importe y plazo, con el resumen («1.000 € · octubre 2027 · unos
 * 84 €/mes») y su barra.
 */
export function HojaObjetivo({ mes, ocupado, onGuardar }: HojaObjetivoProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { eur, eur0 } = useEuros();
  const [meta, setMeta] = useState<MetaElegida>(() => metaInicial('colchon'));
  const [gavi, setGavi] = useState<{
    texto: string | null;
    estado: GaviEstado;
    clave: number;
  }>({ texto: null, estado: 'vuela', clave: 0 });

  const fecha = monthUtils.format(
    monthUtils.addMonths(mes, meta.meses),
    'MMMM yyyy',
    locale,
  );
  const resumen =
    meta.tipo === 'mes'
      ? t('{{amount}} every month', { amount: eur(meta.importe) })
      : t('{{amount}} · {{date}} · about {{monthly}}/month', {
          amount: eur0(meta.importe),
          date: fecha,
          monthly: eur0(cuotaMeta(meta)),
        });
  const avance =
    meta.tipo === 'mes' ? 100 : Math.round(100 / Math.max(1, meta.meses));
  const valido = meta.id !== 'otra' || !!meta.nombre?.trim();

  return (
    <HojaTravesia
      aria-label={t('Your first goal')}
      data-testid="hoja-objetivo"
      cabeza={
        <Bocadillo
          variante="hoja"
          texto={
            gavi.texto ??
            t(
              'An emergency fund turns a broken washing machine into no big deal. The trip, later.',
            )
          }
          estado={gavi.estado}
          clave={gavi.clave}
        />
      }
      pie={
        <Boton
          bloque
          isDisabled={ocupado || !valido}
          data-testid="travesia-guardar-objetivo"
          onPress={() => onGuardar(meta)}
        >
          <Trans>Save goal</Trans>
        </Boton>
      }
    >
      <TituloHoja>
        <Trans>Your first goal</Trans>
      </TituloHoja>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {METAS.map(m => {
          const elegida = meta.id === m.id;
          return (
            <button
              type="button"
              key={m.id}
              aria-pressed={elegida}
              data-testid={`meta-${m.id}`}
              onClick={() => {
                setMeta({ ...metaInicial(m.id), nombre: meta.nombre });
                setGavi(g => ({
                  texto:
                    m.id === 'otra'
                      ? t('A goal with its own name. I like that.')
                      : null,
                  estado: m.id === 'otra' ? 'aplaude' : 'reposo',
                  clave: g.clave + 1,
                }));
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 12px 8px 8px',
                borderRadius: 16,
                backgroundColor: elegida ? color.accentSoft : color.surface2,
                border: `2px solid ${elegida ? color.accent : 'transparent'}`,
                textAlign: 'left',
                minHeight: 64,
                width: '100%',
                color: color.fg,
                fontFamily: 'inherit',
                cursor: 'pointer',
                boxSizing: 'border-box',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <DibujoMeta meta={m.id} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <b style={{ display: 'block', fontSize: 15, lineHeight: 1.25 }}>
                  {m.emoji} {nombreCofre(t, m.id)}
                </b>
                <small
                  style={{
                    display: 'block',
                    color: color.fg3,
                    fontSize: 12.5,
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {descripcionMeta(t, m.id)}
                </small>
              </span>
            </button>
          );
        })}
      </div>
      {meta.id === 'otra' && (
        <div style={{ marginTop: 12 }}>
          <Etiqueta htmlFor="travesia-nombre-meta">
            <Trans>Goal name</Trans>
          </Etiqueta>
          <input
            id="travesia-nombre-meta"
            data-testid="travesia-nombre-meta"
            maxLength={24}
            autoComplete="off"
            value={meta.nombre ?? ''}
            onChange={e => setMeta({ ...meta, nombre: e.target.value })}
            style={estiloCampo}
          />
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <Etiqueta>
          <Trans>Goal</Trans>
        </Etiqueta>
        <Segmentado
          etiqueta={t('Goal')}
          testId="tipo-meta"
          estirar
          alto={40}
          valor={meta.tipo}
          onChange={tipo =>
            setMeta({
              ...meta,
              tipo,
              importe: tipo === 'mes' ? 5000 : 100000,
            })
          }
          opciones={[
            { valor: 'mes', texto: t('Every month') },
            { valor: 'una', texto: t('Once') },
          ]}
          style={{ borderRadius: 14 }}
        />
      </div>
      <div style={{ marginTop: 12 }}>
        <Etiqueta>
          <Trans>I need</Trans>
        </Etiqueta>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(meta.tipo === 'mes' ? IMPORTES_META_MES : IMPORTES_META_UNA).map(
            v => (
              <Chip
                key={v}
                elegido={meta.importe === v}
                data-testid={`importe-meta-${v}`}
                onPress={() => setMeta({ ...meta, importe: v })}
              >
                {eur0(v)}
              </Chip>
            ),
          )}
        </div>
      </div>
      {meta.tipo === 'una' && (
        <div style={{ marginTop: 12 }}>
          <Etiqueta>
            <Trans>Deadline</Trans>
          </Etiqueta>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PLAZOS_META.map(v => (
              <Chip
                key={v}
                elegido={meta.meses === v}
                data-testid={`plazo-meta-${v}`}
                onPress={() => setMeta({ ...meta, meses: v })}
              >
                {t('{{n}} months', { n: v })}
              </Chip>
            ))}
          </div>
        </div>
      )}
      <div
        data-testid="resumen-objetivo"
        style={{
          marginTop: 14,
          padding: '12px 14px',
          borderRadius: 16,
          backgroundColor: color.surface2,
        }}
      >
        <b style={{ display: 'block', fontSize: 15, lineHeight: 1.3 }}>
          {resumen}
        </b>
        <div
          style={{
            height: 8,
            borderRadius: 99,
            backgroundColor: color.surface3,
            overflow: 'hidden',
            marginTop: 10,
          }}
        >
          <i
            style={{
              display: 'block',
              height: '100%',
              width: `${avance}%`,
              backgroundColor: color.accent,
              borderRadius: 99,
              transition: `width .4s ${movimiento.muelle}`,
            }}
          />
        </div>
      </div>
    </HojaTravesia>
  );
}
